import React, {useContext, useEffect, useMemo, useState} from "react"
import {useSearchParams} from "react-router-dom"
import {
    Alert,
    Badge,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Link,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material"
import {LoadingButton} from "@mui/lab"
import AddIcon from "@mui/icons-material/Add"
import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import {
    useApproveAiKnowledgeCandidateMutation,
    useCreateAiKnowledgeNoteMutation,
    useDeleteAiKnowledgeNoteMutation,
    useGetAiKnowledgeCandidatesQuery,
    useGetAiKnowledgeNotesQuery,
    useGetAiKnowledgePendingCountQuery,
    useRejectAiKnowledgeCandidateMutation,
    useUpdateAiKnowledgeNoteMutation
} from "../../core/api/ai-zendesk-settings-api"
import {AI_GAME_LABELS, errorText, formatExact, gameLabel, zendeskTicketUrl} from "../../components/AiTickets/aiTicketsUtils"
import {ConfirmDialog} from "../../components/AiTickets/DecisionDialogs"
import {SnackBarContext} from "../../snackBarProvider"

const textBoxSx = {whiteSpace: "pre-wrap", wordBreak: "break-word"}
const ALL = ""
const IMPROVEMENT_LABEL = {KNOWLEDGE_GAP: "knowledge gap", WRONG_FACTS: "wrong facts"}
const STATUSES = [["NEW", "To review"], ["APPROVED", "Approved"], ["REJECTED", "Rejected"]]

const audit = (at, by) => (at ? `${formatExact(at)}${by ? ` by ${by}` : ""}` : null)

/** Tab label with the number of candidates waiting for a moderator */
export const KnowledgeTabLabel = () => {
    const {data} = useGetAiKnowledgePendingCountQuery(undefined, {pollingInterval: 60000})
    return <Badge badgeContent={data?.pending ?? 0} color="warning" max={99} sx={{"& .MuiBadge-badge": {right: -14, top: 2}}}>Knowledge</Badge>
}

/** Link that opens the ticket in the AI tickets panel (deep link ?ticketId=N) plus the Zendesk link */
export const TicketLinks = ({ticketId}) => {
    const [searchParams, setSearchParams] = useSearchParams()
    if (!ticketId) return null
    const openInPanel = () => {
        const next = new URLSearchParams(searchParams)
        next.set("ticketId", ticketId)
        setSearchParams(next)
    }
    return (
        <Stack direction="row" spacing={0.5} sx={{alignItems: "center"}}>
            <Link component="button" type="button" variant="body2" underline="hover" onClick={openInPanel}>#{ticketId}</Link>
            <Tooltip title="Open in Zendesk">
                <IconButton size="small" component="a" href={zendeskTicketUrl(ticketId)} target="_blank" rel="noopener noreferrer">
                    <OpenInNewIcon sx={{fontSize: 14}}/>
                </IconButton>
            </Tooltip>
        </Stack>
    )
}

/**
 * Note text editor: title = the generic question, body = the rule/fact the bot may use for any player.
 * Used for approving a candidate with edits, editing a note and creating one (askGame + fixed ticketId).
 */
export const NoteDialog = ({open, title, hint, initial, askGame = false, loading, confirmText = "Save", onClose, onSave}) => {
    const [game, setGame] = useState("")
    const [question, setQuestion] = useState("")
    const [fact, setFact] = useState("")
    useEffect(() => {
        if (open) {
            setGame(initial?.gameTag ?? "")
            setQuestion(initial?.title ?? "")
            setFact(initial?.body ?? "")
        }
    }, [open, initial])
    const canSave = !!question.trim() && !!fact.trim() && (!askGame || !!game)
    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {hint && <DialogContentText sx={{mb: 2}}>{hint}</DialogContentText>}
                {askGame && (
                    <TextField select label="Game" size="small" value={game} onChange={(e) => setGame(e.target.value)} sx={{mb: 2, minWidth: 220}}>
                        {Object.entries(AI_GAME_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
                    </TextField>
                )}
                <TextField
                    label="Question it answers (English)" fullWidth size="small" sx={{mb: 2}}
                    value={question} onChange={(e) => setQuestion(e.target.value)}
                    helperText="One generic sentence, e.g. 'Which building produces tempered glass?'"
                />
                <TextField
                    label="Rule / fact (English)" fullWidth multiline minRows={4} maxRows={14} size="small"
                    value={fact} onChange={(e) => setFact(e.target.value)}
                    helperText="General game knowledge only: no player data, ids, amounts or dates"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" disabled={loading}>Cancel</Button>
                <LoadingButton variant="contained" loading={loading} disabled={!canSave}
                               onClick={() => onSave({gameTag: game || initial?.gameTag, title: question.trim(), body: fact.trim()})}>
                    {confirmText}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

/** One candidate from the judge: what the operator knew that the bot did not, with approve / reject. */
export const CandidateCard = ({candidate, compact = false}) => {
    const {addAlert} = useContext(SnackBarContext)
    const [approve, {isLoading: approving}] = useApproveAiKnowledgeCandidateMutation()
    const [reject, {isLoading: rejecting}] = useRejectAiKnowledgeCandidateMutation()
    const [editing, setEditing] = useState(false)
    const decided = candidate.status !== "NEW"

    const run = async (action, successText) => {
        try {
            await action().unwrap()
            addAlert({text: successText, type: "success"})
            return true
        } catch (e) {
            addAlert({text: errorText(e), type: "error"})
            return false
        }
    }
    const onApprove = async ({title, body}) => {
        if (await run(() => approve({id: candidate.id, title, body}), "Added to the knowledge base")) setEditing(false)
    }

    return (
        <Card variant="outlined">
            <CardContent sx={{"&:last-child": {pb: 1.5}}}>
                <Stack direction="row" spacing={0.5} sx={{alignItems: "center", flexWrap: "wrap", rowGap: 0.5, mb: 1}}>
                    {!compact && <Chip size="small" label={gameLabel(candidate.gameTag)} variant="outlined"/>}
                    <Chip size="small" label={IMPROVEMENT_LABEL[candidate.improvementType] ?? candidate.improvementType}
                          color={candidate.improvementType === "WRONG_FACTS" ? "error" : "info"} variant="outlined"/>
                    {decided && <Chip size="small" label={candidate.status.toLowerCase()} color={candidate.status === "APPROVED" ? "success" : "default"}/>}
                    {!compact && <TicketLinks ticketId={candidate.ticketId}/>}
                    <Box sx={{flex: 1}}/>
                    <Typography variant="caption" color="text.secondary">
                        {decided ? audit(candidate.decidedAt, candidate.decidedBy) : formatExact(candidate.createdAt)}
                    </Typography>
                </Stack>
                <Typography variant="subtitle2" sx={textBoxSx}>{candidate.questionEn}</Typography>
                <Typography variant="body2" sx={{...textBoxSx, p: 1.5, bgcolor: "grey.100", borderRadius: 1, mt: 0.5}}>{candidate.factEn}</Typography>
                {candidate.operatorQuote && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{mt: 1, pl: 1.5, borderLeft: 2, borderColor: "divider", ...textBoxSx}}>
                        Operator: “{candidate.operatorQuote}”
                    </Typography>
                )}
                {(candidate.similar ?? []).length > 0 && (
                    <Box sx={{mt: 1}}>
                        <Typography variant="caption" color="text.secondary">Similar in knowledge base:</Typography>
                        <Stack direction="row" spacing={0.5} sx={{flexWrap: "wrap", rowGap: 0.5, mt: 0.25}}>
                            {candidate.similar.map((s) => (
                                <Tooltip key={s.id} title={s.note ? "note from tickets" : "help-center article"}>
                                    <Chip size="small" label={`${s.note ? "note" : "article"}: ${s.title}`} color={s.note ? "secondary" : "default"} variant="outlined"/>
                                </Tooltip>
                            ))}
                        </Stack>
                    </Box>
                )}
                {!decided && (
                    <Stack direction="row" spacing={1} sx={{mt: 1.5}}>
                        <LoadingButton size="small" variant="contained" color="success" startIcon={<CheckIcon/>} loading={approving}
                                       disabled={rejecting} onClick={() => onApprove({title: null, body: null})}>
                            Approve
                        </LoadingButton>
                        <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon/>} disabled={approving || rejecting} onClick={() => setEditing(true)}>
                            Edit & approve
                        </Button>
                        <LoadingButton size="small" variant="outlined" color="error" startIcon={<CloseIcon/>} loading={rejecting} disabled={approving}
                                       onClick={() => run(() => reject({id: candidate.id}), "Candidate rejected")}>
                            Reject
                        </LoadingButton>
                    </Stack>
                )}
                {candidate.noteId && <Typography variant="caption" color="text.secondary">note #{candidate.noteId}</Typography>}
            </CardContent>
            <NoteDialog
                open={editing}
                title="Edit and add to the knowledge base"
                hint="The bot will use this text as an article for every player with the same question."
                initial={{gameTag: candidate.gameTag, title: candidate.questionEn, body: candidate.factEn}}
                loading={approving}
                confirmText="Approve"
                onClose={() => setEditing(false)}
                onSave={onApprove}
            />
        </Card>
    )
}

const GameFilter = ({value, onChange}) => (
    <TextField select size="small" label="Game" value={value} onChange={(e) => onChange(e.target.value)} sx={{minWidth: 220}}>
        <MenuItem value={ALL}>All games</MenuItem>
        {Object.entries(AI_GAME_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
    </TextField>
)

const CandidatesQueue = ({game, setGame}) => {
    const [status, setStatus] = useState("NEW")
    const {data: candidates = [], isFetching, error} = useGetAiKnowledgeCandidatesQuery({status, game: game || undefined})
    return (
        <Stack spacing={1.5}>
            <Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
                <GameFilter value={game} onChange={setGame}/>
                <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{minWidth: 160}}>
                    {STATUSES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
                {isFetching && <CircularProgress size={20}/>}
                <Typography variant="body2" color="text.secondary">{candidates.length} candidate(s)</Typography>
            </Stack>
            {error && <Alert severity="error">{errorText(error)}</Alert>}
            {!error && !isFetching && candidates.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    Nothing here. Candidates appear after a solved ticket with an operator reply is reviewed by the quality judge.
                </Typography>
            )}
            {candidates.map((candidate) => <CandidateCard key={candidate.id} candidate={candidate}/>)}
        </Stack>
    )
}

const NotesList = ({game, setGame}) => {
    const {addAlert} = useContext(SnackBarContext)
    const {data: notes = [], isFetching, error} = useGetAiKnowledgeNotesQuery({game: game || undefined})
    const [createNote, {isLoading: creating}] = useCreateAiKnowledgeNoteMutation()
    const [updateNote, {isLoading: updating}] = useUpdateAiKnowledgeNoteMutation()
    const [deleteNote, {isLoading: deleting}] = useDeleteAiKnowledgeNoteMutation()
    const [search, setSearch] = useState("")
    const [adding, setAdding] = useState(false)
    const [editing, setEditing] = useState(null)
    const [removing, setRemoving] = useState(null)

    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase()
        if (!needle) return notes
        return notes.filter((n) => `${n.title} ${n.body}`.toLowerCase().includes(needle))
    }, [notes, search])

    const run = async (action, successText) => {
        try {
            await action().unwrap()
            addAlert({text: successText, type: "success"})
            return true
        } catch (e) {
            addAlert({text: errorText(e), type: "error"})
            return false
        }
    }

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
                <GameFilter value={game} onChange={setGame}/>
                <TextField size="small" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} sx={{minWidth: 260}}/>
                {isFetching && <CircularProgress size={20}/>}
                <Box sx={{flex: 1}}/>
                <Button variant="contained" size="small" startIcon={<AddIcon/>} onClick={() => setAdding(true)}>Add note</Button>
            </Stack>
            {error && <Alert severity="error">{errorText(error)}</Alert>}
            {!error && !isFetching && filtered.length === 0 && (
                <Typography variant="body2" color="text.secondary">No notes{search ? " match the search" : " yet"}.</Typography>
            )}
            {filtered.map((note) => (
                <Card key={note.id} variant="outlined">
                    <CardContent sx={{"&:last-child": {pb: 1.5}}}>
                        <Stack direction="row" spacing={0.5} sx={{alignItems: "center", flexWrap: "wrap", rowGap: 0.5}}>
                            <Chip size="small" label={gameLabel(note.gameTag)} variant="outlined"/>
                            <Typography variant="caption" color="text.secondary">#{note.id}</Typography>
                            {note.ticketId && <TicketLinks ticketId={note.ticketId}/>}
                            <Box sx={{flex: 1}}/>
                            <Typography variant="caption" color="text.secondary">{audit(note.updatedAt, note.updatedBy ?? note.createdBy)}</Typography>
                            {note.url && (
                                <Tooltip title="Open the article in Zendesk Guide (staff-only section)">
                                    <IconButton size="small" component="a" href={note.url} target="_blank" rel="noopener noreferrer">
                                        <OpenInNewIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => setEditing(note)}><EditOutlinedIcon fontSize="small"/></IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => setRemoving(note)}><DeleteOutlineIcon fontSize="small"/></IconButton>
                            </Tooltip>
                        </Stack>
                        <Typography variant="subtitle2" sx={{...textBoxSx, mt: 0.5}}>{note.title}</Typography>
                        <Typography variant="body2" sx={{...textBoxSx, p: 1.5, bgcolor: "grey.100", borderRadius: 1, mt: 0.5}}>{note.body}</Typography>
                    </CardContent>
                </Card>
            ))}
            <NoteDialog
                open={adding}
                title="New knowledge note"
                hint="Saved as a staff-only Help Center article (category Agent Knowledge): the bot retrieves it for any player whose question matches; operators can edit it in Zendesk Guide."
                initial={{gameTag: game || "toh"}}
                askGame
                loading={creating}
                confirmText="Create"
                onClose={() => setAdding(false)}
                onSave={async (body) => {
                    if (await run(() => createNote(body), "Note created")) setAdding(false)
                }}
            />
            <NoteDialog
                open={!!editing}
                title={`Edit note #${editing?.id ?? ""}`}
                initial={editing}
                loading={updating}
                onClose={() => setEditing(null)}
                onSave={async ({title, body}) => {
                    if (await run(() => updateNote({id: editing.id, title, body}), "Note updated")) setEditing(null)
                }}
            />
            <ConfirmDialog
                open={!!removing}
                title="Delete note?"
                text={`"${removing?.title ?? ""}" will be archived in Zendesk Guide (restorable there) and removed from the bot's knowledge base immediately.`}
                confirmText="Delete"
                loading={deleting}
                onClose={() => setRemoving(null)}
                onConfirm={async () => {
                    if (await run(() => deleteNote({id: removing.id}), "Note deleted")) setRemoving(null)
                }}
            />
        </Stack>
    )
}

/**
 * Knowledge from tickets: the quality judge (runs after a solved ticket answered by an operator) turns the operator's
 * reply into a general game rule -> candidate; a moderator approves it into a note that the bot retrieves
 * like a help-center article. Notes can also be written by hand (here or from the ticket window).
 */
export default function KnowledgeEditor({initialGame = ""}) {
    const [game, setGame] = useState(initialGame)
    const [section, setSection] = useState("candidates")
    const {data: pending} = useGetAiKnowledgePendingCountQuery()
    return (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                General game knowledge learned from tickets. After a solved ticket answered by an operator, the quality judge
                proposes the rule the operator relied on; approve it (with edits if needed) and the bot uses it as an article
                for the next player with the same question. Write only general rules — never facts about one player's account.
            </Typography>
            <Stack direction="row" spacing={1}>
                <Button variant={section === "candidates" ? "contained" : "outlined"} size="small" onClick={() => setSection("candidates")}>
                    To review{pending?.pending ? ` (${pending.pending})` : ""}
                </Button>
                <Button variant={section === "notes" ? "contained" : "outlined"} size="small" onClick={() => setSection("notes")}>
                    Notes
                </Button>
            </Stack>
            {section === "candidates" ? <CandidatesQueue game={game} setGame={setGame}/> : <NotesList game={game} setGame={setGame}/>}
        </Stack>
    )
}
